
import { Collection } from "@/lib/api/types";
import { supabase } from "@/integrations/supabase/client";

export const deleteCollection = async (collection: Collection) => {
  console.log('Starting deletion process for collection:', collection.id);

  try {
    // Use a transaction-like approach with specific collection ID checks
    // First delete all product descriptions associated with this specific collection
    const { error: descriptionsError } = await supabase
      .from("product_descriptions")
      .delete()
      .eq("collection_id", collection.id);

    if (descriptionsError) {
      console.error('Failed to delete product descriptions:', descriptionsError);
      throw descriptionsError;
    }

    console.log('Successfully deleted product descriptions for collection:', collection.id);

    // Then delete the specific collection - use both id and additional safety checks
    const { error: collectionError } = await supabase
      .from("collections")
      .delete()
      .eq("id", collection.id)
      .eq("name", collection.name); // Additional safety check

    if (collectionError) {
      console.error('Failed to delete collection:', collectionError);
      throw collectionError;
    }

    console.log('Successfully deleted collection:', collection.id);
  } catch (error) {
    console.error('Error in deleteCollection:', error);
    throw error;
  }
};

export const getCollectionDescriptions = async (collectionId: string | undefined) => {
  if (!collectionId) return null;
  
  const { data, error } = await supabase
    .from("product_descriptions")
    .select("*")
    .eq("collection_id", collectionId)
    .order("created_at", { ascending: false });

  if (error) throw error;
  return data;
};

export const hasCollectionDescriptions = async (collectionId: string | undefined) => {
  if (!collectionId) return false;

  const { data, error } = await supabase
    .from("product_descriptions")
    .select("id")
    .eq("collection_id", collectionId)
    .limit(1);

  if (error) throw error;
  return data.length > 0;
};

// Enhanced function to fix stuck processing states with comprehensive recovery
export const fixStuckProcessing = async (collectionId: string) => {
  console.log('=== ENHANCED STUCK PROCESSING FIX ===');
  console.log('Attempting to fix stuck processing for collection:', collectionId);
  
  try {
    // First, check if the collection actually has products
    const { data: productCount, error: countError } = await supabase
      .from('product_descriptions')
      .select('id', { count: 'exact' })
      .eq('collection_id', collectionId);

    if (countError) {
      console.error('Error counting products:', countError);
      throw countError;
    }

    const actualCount = productCount?.length || 0;
    console.log(`Found ${actualCount} products for collection ${collectionId}`);

    // Check current progress tracking state
    const { data: currentProgress, error: progressError } = await supabase
      .from('progress_tracking')
      .select('*')
      .eq('id', `upload-${collectionId}`)
      .single();

    if (progressError && progressError.code !== 'PGRST116') {
      console.error('Error fetching progress tracking:', progressError);
      throw progressError;
    }

    console.log('Current progress state:', currentProgress);

    // COMPREHENSIVE FIX: Handle all possible stuck states
    if (actualCount > 0) {
      // Products exist, so processing was successful
      console.log('Products found - marking as completed');
      
      if (currentProgress) {
        // Update existing progress record
        const { error: updateError } = await supabase
          .from('progress_tracking')
          .update({
            status: 'completed',
            current_stage: 'completed',
            current_action: 'completed',
            processed_items: actualCount,
            total_items: actualCount,
            error_details: null,
            notification_sent: false, // Reset to allow notifications
            updated_at: new Date().toISOString(),
            last_updated_at: new Date().toISOString()
          })
          .eq('id', `upload-${collectionId}`);

        if (updateError) {
          console.error('Error updating progress tracking:', updateError);
          throw updateError;
        }
      } else {
        // Create missing progress record
        const { error: createError } = await supabase
          .from('progress_tracking')
          .insert({
            id: `upload-${collectionId}`,
            collection_id: collectionId,
            status: 'completed',
            current_stage: 'completed',
            current_action: 'completed',
            processed_items: actualCount,
            total_items: actualCount,
            process_type: 'product_upload',
            notification_sent: false,
            retries: 0,
            error_details: null,
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString(),
            last_updated_at: new Date().toISOString()
          });

        if (createError) {
          console.error('Error creating progress tracking:', createError);
          throw createError;
        }
      }

      // Update collection status to match
      const { error: collectionError } = await supabase
        .from('collections')
        .update({
          processing_status: 'completed',
          processed_items: actualCount,
          total_items: actualCount
        })
        .eq('id', collectionId);

      if (collectionError) {
        console.error('Error updating collection:', collectionError);
        throw collectionError;
      }

      console.log('Successfully fixed stuck processing state - marked as completed');
    } else {
      // No products exist, processing likely failed
      console.log('No products found - marking as error state');
      
      if (currentProgress) {
        // Update to error state
        const { error: updateError } = await supabase
          .from('progress_tracking')
          .update({
            status: 'error',
            current_stage: 'error',
            current_action: 'error',
            processed_items: 0,
            total_items: 0,
            error_details: JSON.stringify({
              message: 'No products were created during processing',
              timestamp: new Date().toISOString(),
              fixed_by: 'fixStuckProcessing'
            }),
            updated_at: new Date().toISOString(),
            last_updated_at: new Date().toISOString()
          })
          .eq('id', `upload-${collectionId}`);

        if (updateError) {
          console.error('Error updating progress tracking to error:', updateError);
          throw updateError;
        }
      } else {
        // Create error progress record
        const { error: createError } = await supabase
          .from('progress_tracking')
          .insert({
            id: `upload-${collectionId}`,
            collection_id: collectionId,
            status: 'error',
            current_stage: 'error',
            current_action: 'error',
            processed_items: 0,
            total_items: 0,
            process_type: 'product_upload',
            error_details: JSON.stringify({
              message: 'No products were created during processing',
              timestamp: new Date().toISOString(),
              fixed_by: 'fixStuckProcessing'
            }),
            notification_sent: false,
            retries: 0,
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString(),
            last_updated_at: new Date().toISOString()
          });

        if (createError) {
          console.error('Error creating error progress tracking:', createError);
          throw createError;
        }
      }

      // Update collection to error state
      const { error: collectionError } = await supabase
        .from('collections')
        .update({
          processing_status: 'error',
          processed_items: 0,
          total_items: 0
        })
        .eq('id', collectionId);

      if (collectionError) {
        console.error('Error updating collection to error state:', collectionError);
        throw collectionError;
      }

      console.log('Successfully fixed stuck processing state - marked as error');
    }

    return true;
  } catch (error) {
    console.error('Error in enhanced fixStuckProcessing:', error);
    throw error;
  }
};

export const cleanupEmptyItemsAndSyncProgress = async (collectionId: string, itemIdsToDelete: string[]): Promise<void> => {
  console.log(`[cleanupEmptyItems] Removing ${itemIdsToDelete.length} items from collection ${collectionId}`);
  
  // Delete the empty items
  for (const id of itemIdsToDelete) {
    const { error } = await supabase
      .from('product_descriptions')
      .delete()
      .eq('id', id);
    
    if (error) {
      console.error(`[cleanupEmptyItems] Failed to delete item ${id}:`, error);
      throw error;
    }
  }

  // Count remaining items
  const { count, error: countError } = await supabase
    .from('product_descriptions')
    .select('*', { count: 'exact', head: true })
    .eq('collection_id', collectionId);

  if (countError) throw countError;

  const totalItems = count || 0;
  console.log(`[cleanupEmptyItems] Remaining items: ${totalItems}`);

  // Update progress_tracking
  const { error: progressError } = await supabase
    .from('progress_tracking')
    .update({
      total_items: totalItems,
      processed_items: totalItems,
      status: 'completed',
      current_stage: 'completed',
      updated_at: new Date().toISOString(),
      last_updated_at: new Date().toISOString()
    })
    .eq('collection_id', collectionId);

  if (progressError) {
    console.error('[cleanupEmptyItems] Failed to update progress_tracking:', progressError);
    throw progressError;
  }

  // Update collections
  const { error: collectionError } = await supabase
    .from('collections')
    .update({
      total_items: totalItems,
      processed_items: totalItems,
      processing_status: 'completed',
      updated_at: new Date().toISOString()
    })
    .eq('id', collectionId);

  if (collectionError) {
    console.error('[cleanupEmptyItems] Failed to update collections:', collectionError);
    throw collectionError;
  }

  console.log(`[cleanupEmptyItems] ✅ Successfully cleaned up collection. New total: ${totalItems}`);
};
